import {makeAutoObservable, reaction} from 'mobx'
import React from 'react'
import {ObjUtil, Throttle} from 'yougile-util'
import {DataObjectId, TaskColor} from 'yougile-model'
import {DragData} from '@common/drag-data'
import {NestedLevelShift} from '@components/common/sandbox/list-view/constants'
import {
  AddInterfaceConfig,
  AddMode,
  CapturedByListEvent,
  ColConfig,
  ColViewConfig,
  DropPlaceholderState,
  Id,
  Identifiable,
  ListStoreExposedApi,
  ListViewProps,
  ListViewStoreConfig,
  NullableId,
  Placement,
  RowConfig,
  SortSource,
  TemporaryItem
} from '@components/common/sandbox/list-view/types'

interface DraggingState {
  /**
   * ID of the currently dragged item's list
   */
  listId: NullableId
  /**
   * ID of the currently dragged item
   */
  id: NullableId
  aboveNeighbourId: NullableId
  belowNeighbourId: NullableId
}

interface DragGhostState {
  text: string
  visible: boolean
  totalCount: number
  doneCount: number
}

/**
 * Represents the state of the drop target
 */
interface DropTargetState {
  id: NullableId
  before: boolean
}

const DefaultPrimaryColumnConfig: ColViewConfig<Identifiable> = {
  width: 616,
  shortName: '',
  fullName: ''
}

export class ListViewStore<TData extends Identifiable> {
  /**
   * Config contains those props that are used in the rendering process, so it is observed
   * All values are passed to config from the props should be memoized from outside to avoid unnecessary updates
   * For example `doneGetter` prop responsible for the done state of the item and if it is changed, the list should be updated,
   * in this way `doneGetter` should be wrapped to useCallback to avoid unnecessary updates
   * But `doneSetter` is not used in the rendering process, so it is not included in the config
   */
  public config: ListViewStoreConfig<TData>
  public dropPlaceholder: DropPlaceholderState
  /**
   * Set of expanded items (expanded items children are visible)
   */
  public expandedItems: Set<Id>
  /**
   * Set of temporarily expanded items (used to expand items when its add children field is opened)
   */
  public temporaryExpandedItems: Set<Id>
  /**
   * Internal configuration of the add field (enabling, placement, etc.)
   * I can not be changed from the outside, but can be changed by 'create item' button
   */
  public addItemState: AddInterfaceConfig
  /**
   * Represents the currently active item (e.g. item can be activated by open item menu)
   * Activated item is highlighted as hovered even if the mouse is not over it
   */
  public activeItem: NullableId
  /**
   * Represents the item that is highlighted only until the next mouse move
   * It is used to highlight the item right after drop in chrome (to fix the bug with frozen hover state)
   */
  public stillHighlightedItem: NullableId
  /**
   * Current edited item (used for the inline editing). Edited item is also highlighted as hovered
   */
  public editedItem: NullableId
  /**
   * Static state of the dragging process. It is static and non-observable because it is not used in the rendering process
   * and common for all lists.
   */
  public static dragging: DraggingState = {
    listId: null,
    id: null,
    aboveNeighbourId: null,
    belowNeighbourId: null
  }
  /**
   * State of the drop target (when dragging over the item, item ID and position are stored here)
   * Then it used in the drop event to determine the drop position
   */
  public dropTarget: DropTargetState
  /**
   * State of the drag ghost (a floating element that shows the number of dragged items)
   */
  public dragGhost: DragGhostState
  /**
   * Contains currently dragged item ID (used to highlight the item during drag)
   */
  public draggedId: NullableId
  /**
   * Drag ghost element reference (used to set the position of the drag ghost)
   */
  public dragGhostElement: HTMLElement | null = null
  /**
   * Set of parent items that are marked as unlimited (its children are always expanded)
   */
  public unlimitedItems: Set<Id> = new Set()
  /**
   * Non-observable property that holds the body element
   */
  public bodyElement: HTMLElement | null = null
  /**
   * Exposed API for the list component (can be accessed from some handlers and via ref)
   */
  public exposedApi: ListStoreExposedApi
  /**
   * Make list non-interactive (e.g. disable drag and drop, inline editing, hovering, etc.)
   * It is used to fix the Chrome bug with frozen hover state (see https://stackoverflow.com/questions/33814785/hover-appearing-in-the-wrong-position)
   */
  public nonInteractive: boolean
  /**
   * Recently created items (used to highlight them right after the creation)
   * TODO: Wip
   */
  public temporaryCreatedItems: Map<NullableId, TemporaryItem[]>
  /**
   * Contains actual props (non-observable)
   * @private
   */
  private props: ListViewProps<TData>
  /**
   * Helper for debouncing update of the drag state (drop placeholder, drop effect)
   * @private
   */
  private dragStateThrottler = new Throttle(0)
  /**
   * Actual throttled drop effect (none, move, copy, link)
   * @private
   */
  private actualDropEffect: 'none' | 'copy' | 'link' | 'move' = 'none'
  private dragEnterCount = 0
  public constructor(props: ListViewProps<TData>) {
    this.props = props
    this.config = this.selectConfig(props)
    this.dropTarget = {
      id: null,
      before: false
    }
    this.dropPlaceholder = {
      visible: false,
      nestedLevel: 0
    }
    this.addItemState = {
      enabled: false,
      mode: AddMode.BeforeUndoneTasks,
      id: null
    }
    this.activeItem = null
    this.editedItem = null
    this.dragGhost = {
      text: '',
      visible: false,
      totalCount: 0,
      doneCount: 0
    }
    this.draggedId = null
    this.expandedItems = new Set()
    this.temporaryExpandedItems = new Set()
    this.temporaryCreatedItems = new Map<NullableId, TemporaryItem[]>()
    this.exposedApi = {
      rename: (id: DataObjectId | string) => this.rename(id),
      addSubTask: (id: NullableId, mode: AddMode = AddMode.BeforeUndoneTasks) =>
        this.turnOnAddItemEditor(id, mode)
    }
    this.nonInteractive = false
    this.stillHighlightedItem = null
    makeAutoObservable(
      this,
      {
        bodyElement: false,
        exposedApi: false,
        dropTarget: false,
        dragGhostElement: false
      },
      {autoBind: true}
    )

    reaction(
      () => this.addItemState.enabled,
      () => {
        if (this.addItemState.enabled) {
          /**
           * Emit the event when the add field is visible
           */
          this.props.onAddEditorShown?.(this.addItemState.id)
        }
      }
    )
  }

  public get primaryColumnTitle(): React.ReactNode | string {
    return this.config.primaryColumnConfig?.shortName ?? ''
  }

  public get primaryColumnConfig(): ColViewConfig<TData> {
    return {
      ...DefaultPrimaryColumnConfig,
      ...this.config.primaryColumnConfig
    }
  }

  public addTemporarilyCreatedItem(
    parentId: NullableId,
    id: Id,
    name: string,
    mode: AddMode
  ): void {
    if (!this.temporaryCreatedItems.has(parentId)) {
      this.temporaryCreatedItems.set(parentId, [])
    }
    const items = this.temporaryCreatedItems.get(parentId)
    items?.push({id, name, mode})
  }

  public removeTemporarilyCreatedItem(parentId: NullableId, id: Id): void {
    this.temporaryCreatedItems.set(
      parentId,
      this.temporaryCreatedItems
        .get(parentId)
        ?.filter(item => item.id !== id) ?? []
    )
  }

  public cleanupTemporarilyCreatedItems(
    parentId: NullableId,
    rows: RowConfig<TData>[]
  ): void {
    const items = this.temporaryCreatedItems.get(parentId)
    if (items && items.length > 0) {
      this.temporaryCreatedItems.set(
        parentId,
        items?.filter(item => !rows.some(row => row.data.id === item.id)) ?? []
      )
    }
  }

  public getTemporarilyCreatedItems(
    parentId: NullableId,
    addMode: AddMode
  ): TemporaryItem[] {
    return (
      this.temporaryCreatedItems
        .get(parentId)
        ?.filter(item => item.mode === addMode) ?? []
    )
  }

  /**
   * Get the activity status of the item (active items are highlighted as hovered)
   * @param id
   */
  public getIsActive = (id: Id): boolean =>
    this.activeItem === id ||
    this.editedItem === id ||
    this.stillHighlightedItem === id

  /**
   * Updates the store from the new props (called every render)
   * @param props
   */
  public updateFromProps(props: ListViewProps<TData>): void {
    this.props = props
    const nextConfig = this.selectConfig(props)
    if (ObjUtil.shallowEqual(this.config, nextConfig)) {
      // Do not update if the config is the same
      return
    }
    this.config = nextConfig
  }

  /**
   * Proxy method for the onItemClick event from props
   * Should be called when a row is clicked (except for click on function buttons like delete, edit, etc.)
   * @param item
   * @param event
   */
  public onItemClick(
    item: RowConfig<TData>,
    event: React.MouseEvent<HTMLDivElement>
  ): void {
    if (this.props.onItemClick) {
      this.props.onItemClick(item, event)
    }
  }

  /**
   * This method is always called after the body component is mounted
   * Reference to body HTML element is required to calculate the drop placeholder position
   * @param element
   */
  public setBodyElement(element: HTMLElement): void {
    this.bodyElement = element
  }

  public canCreateItemsFor(parentId: NullableId): boolean {
    return this.config.canCreateItemsFor?.(parentId) ?? true
  }

  /**
   * Turn on the add field
   * @param id
   * @param mode
   */
  public turnOnAddItemEditor(id: NullableId, mode: AddMode): void {
    this.addItemState = {
      enabled: true,
      mode,
      id
    }
    if (id) {
      /* Expand the parent item if the add field is placed inside it */
      this.temporarilyExpandChildren(id)
    }
  }

  /**
   * Turn off the add field for specific id and mode
   * @param id
   * @param mode
   */
  public turnOffAddItemEditor(id: NullableId, mode: AddMode): void {
    if (
      this.addItemState.enabled &&
      this.addItemState.id === id &&
      this.addItemState.mode === mode
    ) {
      this.addItemState.enabled = false
    }
    if (id) {
      this.temporarilyCollapseChildren(id)
    }
  }

  /**
   * Turn on item editing
   * @param id
   */
  public turnOnItemEditing(id: Id): void {
    this.editedItem = id
  }

  public turnOffItemEditing(id: Id): void {
    if (this.editedItem === id) {
      this.editedItem = null
    }
  }

  public setDone(data: TData, value: boolean): void {
    this.props.doneSetter(data, value)
  }

  /**
   * Set the active item. Active item is always highlighted as hovered
   * Set null to remove the active state
   * @param id
   */
  public setActiveItem(id: NullableId): void {
    this.activeItem = id
  }

  /**
   * Change the name of the item
   * @param id
   * @param name
   */
  public updateItemName(id: Id, name: string): void {
    this.props.onNameChange?.(id, name)
  }

  /**
   * Mark specific item as unlimited (means all its children are visible)
   * @param id
   */
  public addUnlimitedItem(id: Id): void {
    this.unlimitedItems.add(id)
  }

  /**
   * Remove unlimited state from the item
   * @param id
   */
  public removeUnlimitedItem(id: Id): void {
    this.unlimitedItems.delete(id)
  }

  /**
   * Select the item name
   * @param data
   */
  public selectName(data: TData): string {
    return this.props.selectName(data)
  }

  /**
   * Select the item archived state (it used in the name renderer)
   * @param data
   */
  public selectArchived(data: TData): boolean {
    return this.props.selectArchived?.(data) ?? false
  }

  /**
   * Select the item lazy state (e.g. data is not loaded yet)
   * Lazy state is used in the name renderer (inherited from task title component)
   * @param data
   */
  public selectLazy(data: TData): boolean {
    return this.props.selectLazy?.(data) ?? false
  }

  /**
   * Select the item color. It uses TaskColor because name renderer uses task color classes instead of string colors
   * @param data
   */
  public selectColor(data: TData): TaskColor | null {
    return this.props.selectColor?.(data) ?? null
  }

  public selectTooltipContent(data: TData): string | null {
    return this.props.selectTooltipContent?.(data) ?? null
  }

  /**
   * Decide whether the item is editable
   * @param item
   */
  public isEditingPermittedForItem(item: RowConfig<TData>): boolean {
    return (
      (this.props.selectEditable?.(item.data) ?? true) &&
      typeof this.props.onNameChange === 'function'
    )
  }
  /**
   * Turn on item name editor for given ID
   */
  public rename(id: Id): void {
    this.turnOnItemEditing(id)
  }

  public createItem(
    parentId: NullableId,
    name: string,
    mode: AddMode,
    ignoreEmpty = false
  ): void {
    if (ignoreEmpty && !name.trim()) {
      return
    }
    // Convert add mode to placement
    const placement: Placement =
      mode === AddMode.BeforeUndoneTasks ? Placement.Prepend : Placement.Append
    this.props.onCreateItem?.(parentId, name, placement, (id: Id) => {
      this.addTemporarilyCreatedItem(parentId, id, name, mode)
    })
  }

  /**
   * Whether the item is done
   * @param item
   */
  public isDone(item: RowConfig<TData>): boolean {
    return this.config.doneGetter(item.data)
  }

  public getIsHighlighted(id: Id): boolean {
    return this.config.highlightedItem === id
  }

  public onDragStarted(
    id: Id,
    aboveNeighbourId: NullableId,
    belowNeighbourId: NullableId,
    e: React.DragEvent
  ): void {
    /**
     * Start listening to the global drag end or drop event to handle the end of the dragging process
     * Unfortunately, the drag end event is not triggered when the drop event is handled, and also
     * we can not catch drop event if it happened outside the list.
     * So, listen for global event is only way to handle the end of the dragging process
     */
    document.addEventListener('dragend', this.onGlobalDragEndOrDrop, {
      once: true,
      capture: true
    })
    document.addEventListener('drop', this.onGlobalDragEndOrDrop, {
      once: true,
      capture: true
    })

    /**
     * User can provide a function to create drag data. It may be useful when
     * dragging is monitored or logged (but for now it is not used)
     */
    if (this.props.createDragData) {
      const dragData = this.props.createDragData(id)
      if (dragData) {
        DragData.set(e, dragData)
      }
    }
    ListViewStore.dragging.listId = this.config.listId
    ListViewStore.dragging.id = id
    this.draggedId = id
    /**
     * Memoize the above and below neighbour IDs to disable drop placeholder when dragging over them
     */
    ListViewStore.dragging.aboveNeighbourId = aboveNeighbourId
    ListViewStore.dragging.belowNeighbourId = belowNeighbourId

    this.props.onDragStart?.(id)
  }
  public onDragEnter(e: React.DragEvent, id: Id, nestedLevel: number): void {
    this.dragEnterCount++
    if (
      this.dragEnterCount === 1 &&
      this.internalDragOverHandler(e, id, nestedLevel)
    ) {
      this.props.onDragEnterItem?.(id)
    }
  }

  public onDragOver(e: React.DragEvent, id: Id, nestedLevel: number): void {
    if (this.internalDragOverHandler(e, id, nestedLevel)) {
      this.props.onDragOverItem?.(id)
    }
  }

  public onDragLeave(_: React.DragEvent, id: Id): void {
    this.dragEnterCount = this.dragEnterCount > 0 ? this.dragEnterCount - 1 : 0
    this.dragStateThrottler.run(() => {
      this.dropTarget.id = null
      this.updateDropPlaceholder(false)
    })
    this.props.onDragLeaveItem?.(id)
  }

  public onDragEnded(): void {
    /**
     * Drag end event works only if item was dropped without factual effect (e.g. it was not moved, or moved to the same non-droppable place)
     * Thus, it useless. But it is still here to know that you should not use it, because it won't give you what you want.
     * Use global dragend or drop event listener
     */
  }

  public onDrop(event: React.DragEvent<HTMLDivElement>): void {
    if (!this.dropTarget.id) {
      return
    }
    event.preventDefault()
    event.stopPropagation()

    if (this.dropTarget.id && ListViewStore.dragging.id) {
      this.props.onMoveItem?.(
        ListViewStore.dragging.listId,
        this.config.listId,
        ListViewStore.dragging.id as DataObjectId,
        this.dropTarget.id,
        this.dropTarget.before
      )
    }

    this.dragStateThrottler.run(() => {
      this.updateDropPlaceholder(false)
      this.stillHighlightItem(ListViewStore.dragging.id)
    })
    /**
     * Make it non-interactive to fix Chrome bug with frozen hover state (see https://stackoverflow.com/questions/33814785/hover-appearing-in-the-wrong-position)
     * It will become interactive after the next mouse move
     */
    this.nonInteractive = true

    this.draggedId = null
  }

  /**
   * Handles the drag over event for the list wrapper to prevent blinking of the drop placeholder when it is on the edges
   * @param e
   */
  public onDragOverBody(e: React.DragEvent<HTMLDivElement>): void {
    /* Ignore event already captured by list */
    if ((e as CapturedByListEvent).capturedByList) {
      return
    }
    e.dataTransfer.dropEffect = this.actualDropEffect
    /**
     * Make it interactive again (see the comment in the onDragEnded method)
     */
    if (this.nonInteractive) {
      this.nonInteractive = false
    }
    this.dragStateThrottler.run(() => {
      this.updateDropPlaceholder(true)
    })
  }

  public onBodyDragLeave(e: React.DragEvent<HTMLDivElement>): void {
    e.stopPropagation()
  }
  public onBodyDragEnter(e: React.DragEvent<HTMLDivElement>): void {
    e.stopPropagation()
  }

  /**
   * Block hoisting of the drag over event if list is interactive now
   * @param e
   */
  public onDragEnterLeaveBody(e: React.DragEvent<HTMLDivElement>): void {
    if (!this.nonInteractive) {
      e.stopPropagation()
    }
  }

  /**
   * Special handler that fixes chrome bug with frozen hover state (see https://stackoverflow.com/questions/33814785/hover-appearing-in-the-wrong-position)
   */
  public onMouseMoveOverList(): void {
    if (this.nonInteractive) {
      this.nonInteractive = false
    }
    /**
     * Remove the still highlighted item (see the stillHighlightItem method)
     */
    if (this.stillHighlightedItem) {
      this.stillHighlightedItem = null
    }
  }

  /**
   * Highlight the item until the next mouse move
   * It is used to show the drop position right after the drop, also it fixes the Chrome bug with frozen hover state
   * @param id
   */
  public stillHighlightItem(id: NullableId): void {
    if (!id) {
      return
    }
    this.stillHighlightedItem = id
  }

  public toggleExpandChildren(id: Id): void {
    if (this.expandedItems.has(id)) {
      this.expandedItems.delete(id)
    } else {
      this.expandedItems.add(id)
    }
  }

  /**
   * Expand children temporarily (used to expand children when the add children field is opened)
   * @param id
   */
  public temporarilyExpandChildren(id: Id): void {
    this.temporaryExpandedItems.add(id)
  }

  public temporarilyCollapseChildren(id: Id): void {
    this.temporaryExpandedItems.delete(id)
  }

  public isExpanded(id: Id): boolean {
    return this.expandedItems.has(id) || this.temporaryExpandedItems.has(id)
  }

  public showDragGhost(
    text: string,
    totalCount: number,
    doneCount: number
  ): void {
    this.dragGhost = {
      text,
      visible: true,
      totalCount,
      doneCount
    }
  }

  public hideDragGhost(): void {
    this.dragGhost.visible = false
  }

  public setDragGhostElement(element: HTMLElement | null): void {
    this.dragGhostElement = element
  }

  private onGlobalDragEndOrDrop() {
    if (this.draggedId !== null) {
      this.draggedId = null
    }
    /**
     * Make it non-interactive to fix Chrome bug with frozen hover state (see https://stackoverflow.com/questions/33814785/hover-appearing-in-the-wrong-position)
     * It will become interactive after the next mouse move
     */
    this.nonInteractive = true

    this.dragStateThrottler.run(() => {
      this.updateDropPlaceholder(false)
    })
    const memoizedId = ListViewStore.dragging.id
    ListViewStore.dragging.aboveNeighbourId = null
    ListViewStore.dragging.belowNeighbourId = null

    this.draggedId = null
    this.props.onDragEnd?.(memoizedId ?? '')
  }

  /**
   * Set drop effect state (none, move, copy, link) that will be used in any drop event
   * This method is throttled to avoid blinking of the drop effect when move over the edges
   * @param effect
   * @param e Original drag event (optional)
   * @private
   */
  private setDropEffect(
    effect: 'none' | 'copy' | 'link' | 'move',
    e?: React.DragEvent
  ): void {
    this.dragStateThrottler.run(() => {
      this.actualDropEffect = effect
    })
    if (e) {
      e.dataTransfer.dropEffect = this.actualDropEffect
    }
  }

  /**
   * Internal drag over handler. It calculates the drop placeholder position and updates the state
   * Exactly this we decide where to drop the item and where to show the drop placeholder
   * Data formed by this method is used in the drop event to determine the drop position (see this.dropTarget)
   * @param e
   * @param id
   * @param nestedLevel
   * @private
   */
  private internalDragOverHandler(
    e: React.DragEvent,
    id: Id,
    nestedLevel: number
  ): boolean {
    if (!(e.target instanceof HTMLElement)) {
      return false
    }
    if (this.props.sort && this.props.sort.type !== SortSource.ByCustomOrder) {
      // Return, because the drop is not allowed for the column-based sorting
      this.setDropEffect('none', e)
      return false
    }
    // Find the element with draggable attribute to get correct bounds
    let target = e.target as HTMLElement
    while (target && !target.getAttribute('draggable')) {
      if (target.parentElement === null) {
        this.dragStateThrottler.run(() => {
          this.updateDropPlaceholder(false)
        })
        // Something wrong with the target element. According to the idea, there should not be such a situation
        // But if it happens, we should reset the drop placeholder
        return false
      }
      target = target.parentElement
    }
    ;(e as CapturedByListEvent).capturedByList = true

    const {top, bottom} = target.getBoundingClientRect()
    const center = (top + bottom) / 2
    const before = e.clientY <= center

    const outsideCondition =
      ListViewStore.dragging.id !== null && this.props.canDrop
        ? this.props.canDrop(
            ListViewStore.dragging.listId,
            this.config.listId,
            ListViewStore.dragging.id as Id,
            id,
            nestedLevel,
            before
          )
        : true

    const canBeDroppedHere =
      outsideCondition &&
      id !== ListViewStore.dragging.id &&
      (before
        ? id !== ListViewStore.dragging.belowNeighbourId
        : id !== ListViewStore.dragging.aboveNeighbourId)

    this.setDropEffect(canBeDroppedHere ? 'move' : 'none', e)

    this.dragStateThrottler.run(() => {
      this.dropTarget.id = canBeDroppedHere ? id : null
      this.dropTarget.before = before
      this.updateDropPlaceholder(canBeDroppedHere, nestedLevel, target, before)
    })

    return true
  }

  /**
   * Create grid template for the rows
   * @param columns
   * @param nestedLevel - nesting level of the grid (0 for the top level). Nested level is used to shift the grid to the right
   */
  public createGridTemplate(
    columns: ColConfig<TData>[],
    nestedLevel: number
  ): [string, number] {
    const templateColumns = columns.map(this.buildGridWidthForColumn).join(' ')

    const nestingMargin = nestedLevel * NestedLevelShift
    const template = `${this.buildGridWidthForColumn(
      this.primaryColumnConfig,
      nestingMargin
    )} ${templateColumns}`

    return [template, nestingMargin]
  }

  /**
   * Check whether the item can be dragged
   * @param row Original row
   * @param parentId Item's parent
   * @param nestedLevel Item's nesting level
   */
  public canDrag(
    row: RowConfig<TData>,
    parentId: NullableId,
    nestedLevel: number
  ): boolean {
    return (
      !!row.draggable &&
      (this.props.canDrag?.(
        this.config.listId,
        row.data,
        parentId,
        nestedLevel
      ) ??
        true)
    )
  }

  /**
   * Build grid width for the column. Optionally, you can specify the margin that will be subtracted from the column width.
   * Margin can not be used with 'auto' and 'fr' values. It is used for the primary columns only.
   * @param column
   * @param margin - take away a certain width
   * @private
   */
  private buildGridWidthForColumn(
    column: ColViewConfig<TData>,
    margin = 0
  ): string {
    if (column.width === 'auto') {
      return 'auto' // Auto can not be used with margin
    }
    if (typeof column.width === 'number') {
      return `${column.width - margin}px`
    } else if (typeof column.width === 'string') {
      if (column.width.includes('fr')) {
        return column.width // Fractional width can not be used with margin
      }
      return `calc(${column.width} - ${margin}px)`
    }
    return '1fr' // Default value can not be used with margin too
  }

  private updateDropPlaceholder(
    visible: boolean,
    nestedLevel = 0,
    target?: HTMLElement,
    before?: boolean
  ): void {
    const nextDropPlaceholder = {
      visible: visible,
      nestedLevel: nestedLevel,
      targetElement: target,
      isBefore: before
    }
    if (ObjUtil.shallowEqual(this.dropPlaceholder, nextDropPlaceholder)) {
      return
    }
    this.dropPlaceholder = nextDropPlaceholder
  }

  private selectConfig(
    props: ListViewProps<TData>
  ): ListViewStoreConfig<TData> {
    return {
      listId: props.id || null,
      columns: props.columns,
      doneGetter: props.doneGetter,
      rowRenderer: props.rowRenderer,
      renderItemMenu: props.renderItemMenu,
      primaryColumnConfig: props.primaryColumnConfig,
      allowCreateItems: props.allowCreateItems,
      canCreateItemsFor: props.canCreateItemsFor,
      highlightedItem: props.highlightedItem
    }
  }
}

export const ListContext = React.createContext<
  ListViewStore<Identifiable> | undefined
>(undefined)
export const useListStore = <
  TData extends Identifiable
>(): ListViewStore<TData> => {
  return React.useContext(ListContext) as ListViewStore<TData>
}
